/**
 * The window of a project, brought up (docs/refactor/29): when a command sent with no project runs in one, and when
 * `vephus --project …` asks for one from the command line. The main window when it is already on that project;
 * otherwise that project's own window, opened once and brought back after (desktop/src-tauri: window_for_project).
 *
 * Also what the command line asks for (`vephus --prompt …`): the text goes to this computer's agent through the
 * queue, as if typed in the prompt box, so it shows in every window and on the web.
 */

import { api } from './api'
import { isDesktop } from './desktop'
import { thisMachineId } from './runnerHost'
import type { RepositoryLink } from './types'

/** What a launch of the app asked for (desktop/src-tauri/src/cli.rs). */
export interface CliRequest {
  project?: string | null
  prompt?: string | null
  agent?: string | null
  newWindow?: boolean
}

const MAIN_REPOSITORY_KEY = 'devpulse.activeRepository'

function mainWindowRepository(): string | null {
  try {
    return localStorage.getItem(MAIN_REPOSITORY_KEY)
  } catch {
    return null
  }
}

async function invoke(command: string, args: Record<string, unknown>): Promise<void> {
  const { invoke: call } = await import('@tauri-apps/api/core')
  await call(command, args)
}

/** A project as the command line names it: its name, its id, or its folder on this computer. */
export function findProject(asked: string, linked: RepositoryLink[], folders: Record<string, string>): RepositoryLink | null {
  const wanted = asked.trim().toLowerCase()
  const slash = (path: string) => path.replace(/[\\/]+$/, '').replace(/\\/g, '/').toLowerCase()
  return (
    linked.find((r) => r.id.toLowerCase() === wanted || r.name.toLowerCase() === wanted) ??
    linked.find((r) => folders[r.name] !== undefined && slash(folders[r.name]!) === slash(asked)) ??
    null
  )
}

async function linkedProjects(): Promise<RepositoryLink[]> {
  return (await api.repositories.list()).filter((r) => r.linked)
}

/** Brings up the window of a project, by name: the main window's when it is on it, else its own. */
export async function showProject(name: string): Promise<void> {
  if (!isDesktop()) return
  const project = (await linkedProjects().catch(() => [])).find((r) => r.name.toLowerCase() === name.toLowerCase())
  if (!project) return
  await invoke('window_for_project', { repository: project.id, main: mainWindowRepository() === project.id }).catch(() => undefined)
}

/** Does what `vephus --project … --prompt …` asked. Returns what went wrong, for the log; null when it went. */
export async function handleCli(request: CliRequest, folders: Record<string, string>): Promise<string | null> {
  const linked = await linkedProjects().catch(() => [] as RepositoryLink[])
  const project = request.project ? findProject(request.project, linked, folders) : null
  if (request.project && !project) return `project-not-found:${request.project}`
  if (request.newWindow) await invoke('window_new', { repository: project?.id ?? null })
  else if (project) await invoke('window_for_project', { repository: project.id, main: mainWindowRepository() === project.id })
  if (request.prompt) {
    // To this computer, as if typed here; with no project, this computer chooses one (lib/localRunner.ts).
    await api.promptQueue.send(request.prompt, project?.id ?? null, undefined, thisMachineId(), request.agent ?? undefined)
  }
  return null
}

/** The main window takes what the app was launched with, and listens for later launches (single instance). */
export function startCli(folders: () => Record<string, string>): () => void {
  if (!isDesktop()) return () => {}
  let stop: (() => void) | null = null
  let ended = false
  const run = (request: CliRequest | null) => {
    if (request) void handleCli(request, folders()).then((problem) => problem && console.warn('vephus cli:', problem))
  }
  void (async () => {
    const { invoke: call } = await import('@tauri-apps/api/core')
    run(await call<CliRequest | null>('cli_take').catch(() => null))
    const { listen } = await import('@tauri-apps/api/event')
    const unlisten = await listen<CliRequest>('vephus-cli', (event) => run(event.payload))
    if (ended) unlisten()
    else stop = unlisten
  })()
  return () => {
    ended = true
    stop?.()
  }
}
